/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {GroupedVoiceParticipant} from '@app/components/layout/GroupedVoiceParticipant';
import {VoiceParticipantItem} from '@app/components/layout/VoiceParticipantItem';
import styles from '@app/components/layout/VoiceParticipantsList.module.css';
import {
	createVoiceParticipantSortSnapshot,
	sortVoiceParticipantItemsWithSnapshot,
} from '@app/components/voice/VoiceParticipantSortUtils';
import {Logger} from '@app/lib/Logger';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {GuildRecord} from '@app/records/GuildRecord';
import {UserRecord} from '@app/records/UserRecord';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import LocalVoiceStateStore from '@app/stores/LocalVoiceStateStore';
import UserStore from '@app/stores/UserStore';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import type {VoiceState} from '@app/types/gateway/GatewayVoiceTypes';
import type {User} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useRef} from 'react';

const logger = new Logger('VoiceParticipantsList');

function resolveVoiceParticipantUser(
	userId: string,
	guildId: string,
	states: Array<VoiceState>,
): UserRecord | null {
	// 1. Try UserStore (primary source)
	const storeUser = UserStore.getUser(userId);
	if (storeUser) return storeUser;

	// 2. Try GuildMemberStore (has UserRecord from member data)
	const memberUser = GuildMemberStore.getMember(guildId, userId)?.user;
	if (memberUser) return memberUser;

	// 3. Fallback: create UserRecord from voice state member data
	for (const vs of states) {
		if (vs.member?.user) {
			try {
				const user = new UserRecord(vs.member.user as User);
				UserStore.handleUserUpdate(vs.member.user as User);
				return user;
			} catch {
				logger.warn('Failed to create UserRecord from voice state member data', {userId});
			}
		}
	}

	logger.error('Cannot resolve user for voice participant', {userId, guildId});
	return null;
}

export const VoiceParticipantsList = observer(({guild, channel}: {guild: GuildRecord; channel: ChannelRecord}) => {
	const voiceStates = MediaEngineStore.getAllVoiceStatesInChannel(guild.id, channel.id);
	const currentUser = UserStore.currentUser;
	const localSelfStream = LocalVoiceStateStore.selfStream;
	const groupedSortSnapshotRef = useRef(createVoiceParticipantSortSnapshot());

	// Force MobX to track the entire users object reference so we re-render when any user is cached
	const _usersSnapshot = UserStore.users;

	// Diagnostic: log voice states and user resolution on every render
	const vsEntries = Object.entries(voiceStates);
	if (vsEntries.length > 0) {
		const diagnostics = vsEntries.map(([connId, vs]) => ({
			connId,
			userId: vs.user_id,
			channelId: vs.channel_id,
			inUserStore: !!UserStore.getUser(vs.user_id),
			inMemberStore: !!GuildMemberStore.getMember(guild.id, vs.user_id),
			hasMemberData: !!vs.member?.user,
		}));
		logger.info('Render diagnostics', {
			guildId: guild.id,
			channelId: channel.id,
			voiceStateCount: vsEntries.length,
			currentUserId: currentUser?.id,
			userStoreSize: Object.keys(UserStore.users).length,
			participants: diagnostics,
		});
	}

	useEffect(() => {
		const missingUsers: Array<User> = [];
		for (const vs of Object.values(voiceStates)) {
			if (!UserStore.getUser(vs.user_id)) {
				if (vs.member?.user) {
					missingUsers.push(vs.member.user as User);
				} else {
					logger.warn('Voice participant missing from UserStore and no member data available', {
						userId: vs.user_id,
						guildId: guild.id,
						channelId: channel.id,
						hasMember: !!vs.member,
						memberStoreHas: !!GuildMemberStore.getMember(guild.id, vs.user_id),
					});
				}
			}
		}
		if (missingUsers.length > 0) {
			UserStore.cacheUsers(missingUsers);
		}
	}, [voiceStates, channel.id, guild.id]);

	const grouped = useMemo(() => {
		const byUser = new Map<
			string,
			{
				userId: string;
				states: Array<VoiceState>;
				isCurrentUser: boolean;
				anySpeaking: boolean;
				anyLive: boolean;
			}
		>();

		for (const vs of Object.values(voiceStates)) {
			const userId = vs.user_id;
			let entry = byUser.get(userId);
			if (!entry) {
				entry = {userId, states: [], isCurrentUser: currentUser?.id === userId, anySpeaking: false, anyLive: false};
				byUser.set(userId, entry);
			}
			entry.states.push(vs);

			const connectionId = vs.connection_id ?? '';
			const participant = MediaEngineStore.getParticipantByUserIdAndConnectionId(userId, connectionId);
			const isSelfMuted = vs.self_mute ?? (participant ? !participant.isMicrophoneEnabled : false);
			const isGuildMuted = vs.mute ?? false;
			const speaking = !!(participant?.isSpeaking && !isSelfMuted && !isGuildMuted);
			const live = vs.self_stream === true || (participant ? participant.isScreenShareEnabled : false);

			entry.anySpeaking = entry.anySpeaking || speaking;
			entry.anyLive = entry.anyLive || live;

			if (entry.isCurrentUser) {
				entry.anyLive = entry.anyLive || localSelfStream;
			}
		}

		return sortVoiceParticipantItemsWithSnapshot(Array.from(byUser.values()), {
			snapshot: groupedSortSnapshotRef.current,
			getParticipantKey: (entry) => entry.userId,
			getUserId: (entry) => entry.userId,
			guildId: guild.id,
			channelId: channel.id,
		});
	}, [channel.id, currentUser, guild.id, localSelfStream, voiceStates]);

	if (grouped.length === 0) return null;

	return (
		<div className={styles.container}>
			{grouped.map(({userId, states, isCurrentUser, anySpeaking}) => {
				const user = resolveVoiceParticipantUser(userId, guild.id, states);
				if (!user) return null;

				if (states.length === 1) {
					return (
						<VoiceParticipantItem
							key={userId}
							user={user}
							voiceState={states[0]}
							guildId={guild.id}
							isCurrentUser={isCurrentUser}
						/>
					);
				}

				return (
					<GroupedVoiceParticipant
						key={userId}
						user={user}
						voiceStates={states}
						guildId={guild.id}
						anySpeaking={anySpeaking}
					/>
				);
			})}
		</div>
	);
});
